import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { Captcha } from "components/Captcha";
import { LoadingSpinner } from "components/LoadingSpinner";
import { orderTypeConstants } from "constants/order";
import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { events } from "constants/tagManager";
import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { centsToDollars, dollarsToCents } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { isEmpty } from "lodash";
import { PayCashUnmanaged } from "pages/OrderOverview/components/PayCashUnmanaged";
import { PaymentSuccessFull } from "pages/OrderOverview/components/PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import { PayNowSection } from "pages/OrderOverview/components/PayNowSection";
import {
  StyledBreadCrumb,
  StyledBreadcrumbContainer,
  StyledPageContainer,
} from "pages/OrderUpdate/components/Update/styled";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import { useGetBillingCodeByNameQuery } from "store/queries/billingCode";
import {
  useGetDirectPayLinkPublicConfigQuery,
  useGetMainDirectPayLinkByAccountQuery,
  useCalculateDiscountedAmountMutation,
} from "store/queries/directPayLinks";
import { useCreateOrdersMutation } from "store/queries/order";
import { useCreateOrderHistoryMutation } from "store/queries/orderHistory";
import { onPublicTokenChange } from "store/slices/auth";
import { userSelector } from "store/slices/user";
import { IDirectPayLinkPublicConfig } from "types/DirectPayLink.d";
import { IOrder } from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { ContinueButton } from "./components/ContinueButton";
import { DirectCreate } from "./components/DirectCreate";
import { mapFormToRequestBody } from "./utils/helpers";

function DirectPayInternal() {
  const query = useQuery();
  const { isAuthenticated } = useAuth0();
  const { linkId } = useParams<{ linkId?: string }>();
  const navigate = useNavigate();
  const userState = useSelector(userSelector);
  // Get accountId from URL param (for internal users)
  const accountIdFromQuery = query.get("accountId") as string;

  // Check if this is an internal user (no linkId but has user logged in)
  const isInternalUser = !linkId && !!userState.id?.trim();

  // Fetch link config if linkId is provided (public link)
  const {
    data: linkConfigData,
    isLoading: isLinkConfigLoading,
    error: linkConfigError,
  } = useGetDirectPayLinkPublicConfigQuery(linkId || "", {
    skip: !linkId,
  });

  // Fetch main link for internal users
  const { data: mainLinkData, isLoading: isMainLinkLoading } =
    useGetMainDirectPayLinkByAccountQuery(accountIdFromQuery || "", {
      skip: !isInternalUser || !accountIdFromQuery,
    });

  // Use linkConfig from public link or main link for internal users
  const publicLinkConfig: IDirectPayLinkPublicConfig | null =
    linkConfigData?.data || null;

  // Map main link to IDirectPayLinkPublicConfig format for internal users
  const mainLinkConfig: IDirectPayLinkPublicConfig | null = mainLinkData?.data
    ? {
        shortId: mainLinkData.data.shortId,
        accountId: mainLinkData.data.accountId,
        cptCode: mainLinkData.data.cptCode,
        cptDescription: mainLinkData.data.cptDescription,
        amountInCents: mainLinkData.data.amountInCents,
        amountEditable: mainLinkData.data.amountEditable,
        showAccountNumber: mainLinkData.data.showAccountNumber,
        showAccountLocation: mainLinkData.data.showAccountLocation,
        showProviderName: mainLinkData.data.showProviderName,
        providers:
          mainLinkData.data.providers?.map((p) => ({
            id: p.id,
            name: p.name,
          })) || [],
        header: mainLinkData.data.header,
        subheader: mainLinkData.data.subheader,
        isActive: mainLinkData.data.isActive,
        isExpired: false, // Main links don't expire
        ordersCreated: mainLinkData.data.ordersCreated,
        maxOrders: mainLinkData.data.maxOrders || 0,
        accountLogoUrl: null,
        showMarkPaidViaCash: mainLinkData.data.showMarkPaidViaCash,
        orderPaymentNotesEnabled: mainLinkData.data.orderPaymentNotesEnabled,
      }
    : null;

  const linkConfig = publicLinkConfig || mainLinkConfig;

  // Get accountId from either URL param or link config
  const accountId = linkConfig?.accountId || accountIdFromQuery;

  // For public links, skip authenticated queries
  const isPublicLink = !!linkId;

  const methods = useForm<{
    lastName: string | null;
    firstName: string | null;
    phoneNumber: string | null;
    dateOfBirth: string | null;
    dateOfService: string | null;
    timeOfService: string | null;
    accountNumber: string | null;
    accountLocationId: string | null;
    providerId: string | null;
    totalAmount: number | null;
  }>({
    defaultValues: {
      lastName: null,
      firstName: null,
      phoneNumber: null,
      dateOfBirth: null,
      dateOfService: parseDate(new Date(), false, true),
      timeOfService: null,
      accountNumber: null,
      accountLocationId: null,
      providerId: null,
      totalAmount: null,
    },
  });
  const [useCreateOrder, { isLoading }] = useCreateOrdersMutation();
  const [calculateAmount] = useCalculateDiscountedAmountMutation();
  const [createOrderHistory] = useCreateOrderHistoryMutation();

  // Update form values when linkConfig loads for public links or internal users
  const linkAmountInCents = linkConfig?.amountInCents;
  const { setValue } = methods;
  useEffect(() => {
    if (linkAmountInCents) {
      const amount = centsToDollars(linkAmountInCents);
      setValue("totalAmount", amount);
    }
  }, [linkAmountInCents, setValue]);

  // Use CPT code from link config or default to G0463
  const cptCode = linkConfig?.cptCode || "G0463";
  const { data: billingCode, isLoading: isBillingCodeLoading } =
    useGetBillingCodeByNameQuery(cptCode, {
      skip: isPublicLink, // Skip for public links (use linkConfig data instead)
    });

  const dispatch = useDispatch();
  const { current: toastElement } = useContext(ToastContext);
  const { data: accountData, isLoading: isAccountLoading } =
    useGetAccountByIdQuery(accountId, {
      skip: isEmpty(accountId) || isPublicLink, // Skip for public links
    });
  const [showPaymentSection, setShowPaymentSection] = useState({
    paymentPlan: false,
    paymentSection: false,
    paymentSuccess: false,
    receiptAvailable: false,
  });
  const [showCashPaymentSection, setShowCashPaymentSection] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<IOrder | null>(null);
  const [prices, setPrices] = useState<{
    clearPrice: number;
    totalSaving: number;
    fullPrice: number;
  } | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [clickedButton, setClickedButton] = useState<
    "credit_card" | "cash" | null
  >(null);
  const [note, setNote] = useState<string>("");
  const [noteDraft, setNoteDraft] = useState<string>("");
  const [showNoteDialog, setShowNoteDialog] = useState(false);

  const openNoteDialog = () => {
    setNoteDraft(note);
    setShowNoteDialog(true);
  };

  const handleAddNote = () => {
    setNote(noteDraft);
    setShowNoteDialog(false);
  };
  const { pageViewEvents } = useAnalytics();
  const { getClearPriceByOrderType } = usePrice();
  const [login] = useLoginWithOrderIdAndDOBMutation();

  const breadcrumbItems = useMemo(() => {
    // Don't show breadcrumbs for public links
    if (isPublicLink) {
      return [];
    }
    const payFullyItem = {
      label: "Pay",
      template: <span className="active">Pay</span>,
    };
    const createOrderItem = {
      label: "Order details",
      template: <span className="active">Order details</span>,
    };
    return [
      {
        label: "Orders",
        template: (
          <Button
            onClick={() => {
              navigate(`/hospitals/view/${accountId}`);
            }}
            className="p-button-text"
          >
            Orders
          </Button>
        ),
      },
      ...(showPaymentSection.paymentSection || showCashPaymentSection
        ? [payFullyItem]
        : [createOrderItem]),
    ];
  }, [accountId, showPaymentSection, showCashPaymentSection, isPublicLink]);
  console.log("isAuthenticated", isAuthenticated);

  const showContinueToCashButton = useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }
    if (mainLinkConfig) {
      return mainLinkConfig.showMarkPaidViaCash;
    }
    if (linkConfig) {
      return linkConfig?.showMarkPaidViaCash;
    }
    return accountData?.data?.features?.markPaidViaCashUnmanaged;
  }, [
    mainLinkConfig,
    accountData?.data?.features?.markPaidViaCashUnmanaged,
    linkConfig,
    isAuthenticated,
  ]);

  const showOrderPaymentNotesEnabled = useMemo(() => {
    if (!createdOrder) {
      return false;
    }
    if (isInternalUser) {
      return mainLinkConfig
        ? mainLinkConfig?.orderPaymentNotesEnabled
        : accountData?.data?.features?.orderPaymentNotesEnabled;
    }
    return linkConfig?.orderPaymentNotesEnabled;
  }, [
    mainLinkConfig,
    accountData?.data?.features?.orderPaymentNotesEnabled,
    linkConfig,
    isInternalUser,
    createdOrder,
  ]);

  const publicContact = () => {
    if (!createdOrder) {
      return {
        isVisible: false,
        name: "",
        role: "",
        phone: "",
      };
    }
    const currentProvider = createdOrder?.account
      .providers[0] as unknown as IProvider;

    const isVisible =
      currentProvider.publicContactName &&
      currentProvider.publicContactRole &&
      currentProvider.publicContactPhone;

    return {
      isVisible,
      name: currentProvider?.publicContactName,
      role: currentProvider?.publicContactRole,
      phone: currentProvider?.publicContactPhone,
    };
  };
  const handleContinue = async (paymentType: "credit_card" | "cash") => {
    if (!accountId) {
      return;
    }

    // For public links, billingCode will be undefined (we use linkConfig instead)
    if (!isPublicLink && !billingCode) {
      return;
    }

    // Check CAPTCHA for generated links
    if (linkId && !captchaToken) {
      toastElement?.show({
        severity: "warn",
        summary: "CAPTCHA Required",
        detail: "Please complete the CAPTCHA verification",
      });
      return;
    }

    setClickedButton(paymentType);

    const values = methods.getValues();
    const originalAmount = values.totalAmount || 0;

    try {
      // Step 1: Calculate discounted amount via backend
      const amountResponse = await calculateAmount({
        accountId,
        amountInCents: dollarsToCents(originalAmount),
        orderType: orderTypeConstants.PATIENT_RESPONSIBILITY,
      }).unwrap();

      // Extract both original and discounted amounts
      const originalAmountInCents = Math.round(
        amountResponse.data.originalAmountInCents || 0
      );
      const discountedAmountInCents = Math.round(
        amountResponse.data.finalAmountInCents || 0
      );

      if (discountedAmountInCents <= 0 || originalAmountInCents <= 0) {
        toastElement?.show({
          severity: "error",
          summary: "Invalid Amount",
          detail: "Calculated amount is invalid. Please try again.",
        });
        return;
      }

      // Step 2: Create order with original price and discounted patient responsibility
      const requestBody = mapFormToRequestBody(
        values,
        accountId,
        billingCode?.data,
        originalAmountInCents,
        discountedAmountInCents,
        linkId || mainLinkData?.data?.shortId,
        linkConfig,
        captchaToken
      );

      useCreateOrder(requestBody)
        .unwrap()
        .then((createdOrder) => {
          pageViewEvents(
            {
              accountName: accountData?.data.name || "",
              orderType: orderTypeConstants.PATIENT_RESPONSIBILITY,
              communicationMethod: "Phone",
            },
            events.DIRECT_PAY_ORDER_CREATED
          );
          setCreatedOrder(createdOrder.data);
          const prices = getClearPriceByOrderType({
            order: createdOrder.data,
          });
          const clearPrice = centsToDollars(prices.clearPrice);
          const totalSaving = centsToDollars(prices.totalSaving);
          const fullPrice = centsToDollars(prices.total);
          setPrices({ clearPrice, totalSaving, fullPrice });
          login({
            orderId: createdOrder.data.externalId || "",
            dateOfBirth: new Date(
              createdOrder.data.patient.dateOfBirth
            ).toISOString(),
          })
            .unwrap()
            .then(async ({ token }) => {
              dispatch(onPublicTokenChange({ token }));
              // If an internal user attached a note, record it as an order
              // history entry (NotesAdded) before advancing to the next page.
              const trimmedNote = note.trim();
              if (trimmedNote) {
                try {
                  await createOrderHistory({
                    orderId: createdOrder.data.id,
                    type: orderHsitoryTypeConstants.NotesAdded,
                    note: trimmedNote,
                    userId: userState.id || undefined,
                  }).unwrap();
                } catch {
                  toastElement?.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to save the note. Please try again.",
                  });
                  setClickedButton(null);
                  return;
                }
              }
              if (paymentType === "credit_card") {
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: true,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowCashPaymentSection(false);
              } else {
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: false,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowCashPaymentSection(true);
              }
            })
            .catch(() => {
              const detail = "Authentication failed. Please try again.";

              toastElement?.show({
                detail,
                summary: "Error",
                severity: "error",
              });
            });
        })
        .catch((error) => {
          // Handle specific backend error codes
          let errorMessage =
            "An error has occurred attempting to create the Order";

          // Try to extract error from different possible locations
          const backendMessage =
            error?.data?.messages?.[0] || // Backend sends messages as array
            error?.data?.message ||
            error?.message ||
            "";
          let errorCode = error?.data?.code || error?.code;

          // Extract DP code from message like "... (DP003)"
          if (!errorCode && backendMessage) {
            const codeMatch = backendMessage.match(/\(DP\d{3}\)/);
            if (codeMatch) {
              errorCode = codeMatch[0].replace(/[()]/g, ""); // Remove parentheses
            }
          }

          if (errorCode) {
            switch (errorCode) {
              case "DP001":
                errorMessage = "Payment link not found or invalid";
                break;
              case "DP002":
                errorMessage = "Payment link has expired";
                break;
              case "DP003":
                errorMessage =
                  "This payment link has been blocked due to suspicious activity. Please contact the hospital for assistance.";
                break;
              case "DP004":
                errorMessage = "CAPTCHA verification failed. Please try again";
                break;
              case "DP005":
                errorMessage = "Direct Pay is not enabled for this hospital";
                break;
              case "DP006":
                errorMessage =
                  "Payment link has reached its maximum order limit";
                break;
              default:
                // Use backend message if available, clean it up
                if (backendMessage) {
                  errorMessage = backendMessage.replace(
                    /\s*\(DP\d{3}\)\s*$/,
                    ""
                  );
                }
                break;
            }
          } else if (backendMessage) {
            // Clean up backend message - remove code if present
            errorMessage = backendMessage.replace(/\s*\(DP\d{3}\)\s*$/, "");
          }

          toastElement?.show({
            severity: "error",
            summary: "Payment Error",
            detail: errorMessage,
          });
          setClickedButton(null);
        });
    } catch (error: any) {
      // Handle error from calculateAmount
      const errorMessage =
        error?.data?.message || "Failed to calculate amount. Please try again.";
      toastElement?.show({
        severity: "error",
        summary: "Calculation Error",
        detail: errorMessage,
      });
      setClickedButton(null);
    }
  };

  // Check if there's an error fetching link config
  if (linkId && linkConfigError) {
    return (
      <div className="flex flex-column w-full align-items-center justify-content-center p-5">
        <h2>Payment Link Unavailable</h2>
        <div className="text-center max-w-30rem">
          <p className="mb-3">
            This payment link is not available or cannot be accessed at this
            time.
          </p>
          <div className="text-sm text-gray-600">
            <p className="mt-3">Please contact the hospital for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if link is expired or invalid
  if (linkId && linkConfig && (linkConfig.isExpired || !linkConfig.isActive)) {
    return (
      <div className="flex flex-column w-full align-items-center justify-content-center p-5">
        <h2>Payment Link Unavailable</h2>
        <div className="text-center max-w-30rem">
          {linkConfig.isExpired ? (
            <div>
              <p className="mb-3">This payment link has expired.</p>
              <div className="text-sm text-gray-600">
                <p>Links expire when either:</p>
                <ul className="list-none p-0 mt-2">
                  <li>• The expiration date is reached</li>
                  <li>• The maximum number of orders is reached</li>
                </ul>
                <p className="mt-3">
                  Please contact the hospital for a new payment link.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <p className="mb-3">This payment link is no longer active.</p>
              <div className="text-sm text-gray-600">
                <p>This link may have been:</p>
                <ul className="list-none p-0 mt-2">
                  <li>• Deactivated by the hospital</li>
                  <li>• Automatically blocked due to suspicious activity</li>
                  <li>• Disabled for maintenance</li>
                </ul>
                <p className="mt-3">
                  Please contact the hospital for assistance.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loader for public links until linkConfig is loaded (but not if there's an error)
  // Show loader for internal users until main link is loaded
  // Show loader for internal links until accountData is loaded
  if (
    (isPublicLink &&
      !linkConfigError &&
      (!linkConfig || isLinkConfigLoading)) ||
    (isInternalUser && isMainLinkLoading) ||
    (!isPublicLink && !isInternalUser && isAccountLoading)
  ) {
    return <LoadingSpinner />;
  }

  if (showPaymentSection.paymentSuccess && createdOrder) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={createdOrder?.externalId || ""}
        showReceiptMessage={!showCashPaymentSection}
        receiptAvailable={showPaymentSection.receiptAvailable}
        orderUrl={
          userState.currentAccount ? `/orders/${createdOrder.id}` : undefined
        }
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-column w-full align-items-center">
        {!isPublicLink && (
          <StyledBreadcrumbContainer className="p-3">
            <StyledBreadCrumb model={breadcrumbItems} />
          </StyledBreadcrumbContainer>
        )}
        <StyledPageContainer
          maxWidth={
            showPaymentSection.paymentSection || showCashPaymentSection
              ? "550px"
              : undefined
          }
          minWidth={
            showPaymentSection.paymentSection || showCashPaymentSection
              ? "550px"
              : undefined
          }
        >
          {!showPaymentSection.paymentSection && !showCashPaymentSection && (
            <>
              {/* Header and Subheader - use dynamic values from linkConfig or defaults */}
              <div className="text-center mb-4">
                <h1 className="text-2xl font-bold mb-2">
                  {linkConfig?.header || "Pay Now to Confirm Your Appointment"}
                </h1>
                <p className="text-base text-gray-600">
                  {linkConfig?.subheader ||
                    "Complete the form below to pay for your visit"}
                </p>
              </div>
              <DirectCreate
                linkConfig={linkConfig}
                isInternalUser={isInternalUser}
                account={accountData?.data}
              />
              {linkId && (
                <div className="flex justify-content-center my-3">
                  <Captcha
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken("")}
                    onError={() => setCaptchaToken("")}
                  />
                </div>
              )}
              {isAuthenticated &&
                (note ? (
                  <div
                    className="flex align-items-center justify-content-between mb-3 p-3 border-1 border-round-lg"
                    style={{ borderColor: "#e5e7eb", background: "#fff" }}
                  >
                    <div className="flex align-items-center gap-3">
                      <i className="pi pi-file-edit purple" />
                      <div>
                        <div className="font-bold">Note</div>
                        <div className="text-600 white-space-pre-wrap">
                          {note}
                        </div>
                      </div>
                    </div>
                    <div className="flex align-items-center gap-1">
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-sm"
                        tooltip="Edit"
                        tooltipOptions={{ position: "top" }}
                        aria-label="Edit note"
                        onClick={openNoteDialog}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-text p-button-sm p-button-danger"
                        tooltip="Delete"
                        tooltipOptions={{ position: "top" }}
                        aria-label="Delete note"
                        onClick={() => setNote("")}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-content-center mb-3">
                    <Button
                      className="p-button-link p-0"
                      icon="pi pi-plus"
                      label="Add note"
                      onClick={openNoteDialog}
                    />
                  </div>
                ))}
              <div className="flex flex-column">
                <ContinueButton
                  onClick={() => handleContinue("credit_card")}
                  loading={isLoading && clickedButton === "credit_card"}
                  disabled={
                    isBillingCodeLoading ||
                    (isLoading && clickedButton !== "credit_card")
                  }
                >
                  Continue to credit card
                </ContinueButton>
                {showContinueToCashButton && (
                  <ContinueButton
                    onClick={() => handleContinue("cash")}
                    loading={isLoading && clickedButton === "cash"}
                    disabled={
                      isBillingCodeLoading ||
                      (isLoading && clickedButton !== "cash")
                    }
                    variant="secondary"
                    className="mt-0"
                  >
                    Continue to cash payment
                  </ContinueButton>
                )}
              </div>
            </>
          )}
          {showCashPaymentSection && createdOrder && (
            <PayCashUnmanaged
              clearPrice={prices?.clearPrice ?? 0}
              isInternalUser={isInternalUser}
              orderId={createdOrder.id}
              captchaToken={captchaToken}
              directPayLinkId={linkId}
              setShowPaymentSection={setShowPaymentSection}
              showOrderPaymentNotesEnabled={!!showOrderPaymentNotesEnabled}
            />
          )}
          {showPaymentSection.paymentSection && createdOrder && (
            <PayNowSection
              {...{
                expirationDays:
                  createdOrder?.account.providers[0].expirationDays,
                orderInfo: createdOrder,
                showPaymentSection,
                setShowPaymentSection,
                clearPrice: prices?.clearPrice || 0,
                totalSaving: prices?.totalSaving || 0,
                fullPrice: prices?.fullPrice || 0,
                payRemainingPrice: false,
                orderExternalId: createdOrder.externalId,
              }}
            />
          )}
        </StyledPageContainer>
      </div>
      <Dialog
        header="Add note"
        visible={showNoteDialog}
        onHide={() => setShowNoteDialog(false)}
        style={{ width: "32rem" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowNoteDialog(false)}
            />
            <Button label="Save" onClick={handleAddNote} />
          </div>
        }
      >
        <InputTextarea
          autoResize
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Enter a note"
          rows={5}
          style={{ width: "100%" }}
        />
      </Dialog>
    </FormProvider>
  );
}

// Create the protected version once
const ProtectedDirectPayInternal = withAuthenticationRequired(
  DirectPayInternal,
  {
    returnTo: "/order/direct-pay",
  }
);

// Only require Auth0 when accessed internally (without linkId)
// Public links (with linkId) bypass Auth0
export function DirectPay() {
  const { linkId } = useParams<{ linkId?: string }>();

  // If there's a linkId, this is a public link - no Auth0 required
  // Return DirectPayInternal directly without any Auth0 checks
  if (linkId) {
    return <DirectPayInternal />;
  }

  // If no linkId, this is internal access - require Auth0
  return <ProtectedDirectPayInternal />;
}
